// Helpers for JSON Merge Patch–style submits driven by react-hook-form's
// `dirtyFields`. The contract: a deparse method walks the dirty-field tree
// and only emits keys whose user-touched state is true. Untouched keys are
// omitted; explicit clears are emitted as `null`. This is the foundation for
// the form-fidelity work — see plan in form-fidelity-resilience.md.

/**
 * Recursive check: returns true if any leaf in the dirty tree is truthy.
 * Handles RHF's nested object / array dirty shapes.
 */
export const hasAnyDirty = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (Array.isArray(value)) return value.some(hasAnyDirty);
  if (typeof value === 'object' && value !== null) {
    return Object.values(value).some(hasAnyDirty);
  }
  return Boolean(value);
};

/**
 * Returns true if `key` (or any nested field under it) was touched.
 * Pass RHF's `formState.dirtyFields` as the first arg.
 */
export const isFieldDirty = <T extends object>(
  dirtyFields: Partial<Record<keyof T, unknown>> | undefined,
  key: keyof T,
): boolean => {
  if (!dirtyFields) return false;
  return hasAnyDirty(dirtyFields[key]);
};

/**
 * Returns true if any of the given keys (or any nested field under them) were
 * touched. Useful for "field groups" — multiple form fields that map to a
 * single API key (e.g. `circleCenter` + `radius` + `drawingCoordinates` →
 * `next_billion_geofence`).
 */
export const isAnyFieldDirty = <T extends object>(
  dirtyFields: Partial<Record<keyof T, unknown>> | undefined,
  keys: (keyof T)[],
): boolean => keys.some((k) => isFieldDirty(dirtyFields, k));
